import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';

interface AgeDistributionData {
  label: string;
  value: number;
  color?: string;
}

interface AgeDistributionChartProps {
  data: AgeDistributionData[];
  height?: number;
}

const CHART_COLORS = ['#4DD0E1', '#FF9800', '#1976D2', '#4CAF50', '#F44336'];

export const AgeDistributionChart: React.FC<AgeDistributionChartProps> = ({ data, height = 200 }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const total = data.reduce((acc, item) => acc + item.value, 0);
  const formatPercentage = (value: number) => {
    if (!Number.isFinite(value)) return '0%';
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
  };

  // Config for rings
  // Use width if available, otherwise fallback to height (for initial render)
  // Allow the chart to scale up to 1.5x height to maximize space usage
  const containerWidth = width ? Math.min(width, height * 1.5) : height;
  const padding = 12;
  const basis = Math.max(0, containerWidth - padding);
  const radiusWidth = basis / 8;
  const gap = radiusWidth / 4;
  const maxRadius = basis / 2; // Base radius size, will scale

  const series = data.map((item, index) => {
    const outerRadius = maxRadius - index * (radiusWidth + gap);
    const innerRadius = outerRadius - radiusWidth;
    const color = item.color || CHART_COLORS[index % CHART_COLORS.length];
    const percentage = total > 0 ? (item.value / total) * 100 : 0;

    return {
      data: [
        {
          value: item.value,
          color: color,
          label: item.label,
          percentage,
        },
        {
          value: total - item.value,
          color: theme.palette.action.disabledBackground,
          tooltip: 'none', // Hide tooltip for background segment
        },
      ],
      valueFormatter: () => formatPercentage(percentage),
      innerRadius,
      outerRadius,
      startAngle: -90,
      endAngle: 90,
      cx: '50%',
      cy: '100%',
      paddingAngle: 0,
      cornerRadius: 4,
    };
  });

  // Compute label positions ON the colored arc (midpoint angle, mid-radius)
  const labelPositions = useMemo(() => {
    if (!width) return [];

    const cx = width / 2;
    const cy = height - 40;

    return data.map((item, index) => {
      const outerRadius = maxRadius - index * (radiusWidth + gap);
      const innerRadius = outerRadius - radiusWidth;
      const percentage = total > 0 ? (item.value / total) * 100 : 0;
      // Midpoint angle of the colored arc
      const startAngleDeg = -90;
      const sweepDeg = (percentage / 100) * 180;
      const midAngleDeg = startAngleDeg + sweepDeg / 2;
      const midAngleRad = (midAngleDeg * Math.PI) / 180;

      // Position at the center of the ring band at the midpoint angle
      const midRadius = (innerRadius + outerRadius) / 2;
      const x = cx + midRadius * Math.sin(midAngleRad);
      const y = cy - midRadius * Math.cos(midAngleRad);

      return {
        x,
        y,
        text: formatPercentage(percentage),
        textAnchor: 'middle' as const,
      };
    });
  }, [data, width, height, maxRadius, radiusWidth, gap, total]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <PieChart
        series={series}
        height={height}
        width={width}
        margin={{ top: 0, right: 0, bottom: 40, left: 0 }}
        slotProps={{
          pieArc: { strokeWidth: 0 },
          legend: {
            direction: 'horizontal',
            position: { vertical: 'bottom', horizontal: 'center' },
          },
        }}
      />
      {width > 0 && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: width,
            height: height - 40,
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          {labelPositions.map((pos, i) => (
            <text
              key={i}
              x={pos.x}
              y={pos.y}
              textAnchor={pos.textAnchor}
              dominantBaseline='middle'
              fill={theme.palette.text.primary}
              fontSize={12}
              fontWeight={600}
              fontFamily={theme.typography.fontFamily as string}
            >
              {pos.text}
            </text>
          ))}
        </svg>
      )}
    </Box>
  );
};
